import React, { useEffect, useRef, useState } from "react";
import { apiCall } from "../utils/Api";
import { Button, Spin, Input } from "antd";
import { PlusOutlined, MinusOutlined } from "@ant-design/icons";
import { useParams } from "react-router-dom";

const DEBOUNCE_DELAY = 300;

const PDFViewer = (props) => {

	const { drawComponent = () => {}, isbn = "" } = props;
	const { url_isbn } = useParams();
	const canvasRef = useRef(null);
	const containerRef = useRef(null);
	const [pdf, setPdf] = useState(null);
	const [pageNum, setPageNum] = useState(1);
	const [totalPages, setTotalPages] = useState(null);
	const [scale, setScale] = useState(1);
	const [ready, setReady] = useState(false);
	const [pdfData, setPdfData] = useState();
	const [inputPage, setInputPage] = useState(1);


	const getPage = () => {
		apiCall('drc/getpage', {isbn: isbn ? isbn : url_isbn, page_num: pageNum}, (_status, _result) => {
			if (_status) {
			if (_result.result) {
				setPdfData(atob(_result.result));
			}
			}
		})
	}

	const getTotalPages = () => {
		apiCall('drc/getdetails', {isbn: isbn ? isbn : url_isbn, page_num: pageNum}, (_status, _result) => {
			if (_status) {
			setTotalPages(_result.pages);
			}
		})
	}

	const loadPdf = async () => {
		if (!pdfData) {
			return
		}
		const loadingTask = window.pdfjsLib.getDocument({data: pdfData, wasmUrl: `https://unpkg.com/pdfjs-dist@5.1.91/wasm/`,});
		const loadedPdf = await loadingTask.promise;
		setPdf(loadedPdf);
	};

	useEffect(() => {
		let renderTask;
		let cancelled = false;

		const doRender = async () => {
			if (!pdf) return;
			const page = await pdf.getPage(1);

			// Auto-fit scale calculation
			if (containerRef.current && scale === 1) { 
				const viewport = page.getViewport({ scale: 1 });
				const containerWidth = containerRef.current.offsetWidth - 10;
				const containerHeight = url_isbn ? window.innerHeight - 150 : containerRef.current.offsetHeight - 10;
				const fitByWidth = containerWidth / viewport.width;
				const fitByHeight = containerHeight > 0 ? containerHeight / viewport.height : Infinity;
				const fitScale = Math.min(fitByWidth, fitByHeight);
				setScale(fitScale > 3 ? 3 : fitScale);
				return;
			}

			const viewport = page.getViewport({ scale });

			const canvas = canvasRef.current;
			const context = canvas.getContext("2d");

			canvas.height = viewport.height;
			canvas.width = viewport.width;

			renderTask = page.render({ canvasContext: context, viewport });
			try {
				await renderTask.promise;
			} catch (err) {
			}
		};

		doRender();

		return () => {
			cancelled = true;
			if (renderTask && renderTask.cancel) {
			renderTask.cancel();
			}
		};

	}, [scale, pdf]);

		
	useEffect(() => {
		setInputPage(pageNum);
	}, [pageNum]);

	useEffect(() => {
		getTotalPages();
	}, []);

	useEffect(() => {
		const handler = setTimeout(() => {
			getPage();
		}, DEBOUNCE_DELAY);

		return () => clearTimeout(handler);
	}, [pageNum]);

    useEffect(() => {
        if (!pdfData) return;
        const handler = setTimeout(() => {
            loadPdf();
        }, DEBOUNCE_DELAY);

        return () => clearTimeout(handler);
    }, [pdfData]);

	const nextPage = () => {
		setPageNum(prev => prev + 1);
	};

	const prevPage = () => {
		if (pageNum > 1) setPageNum(prev => prev - 1);
	};

	const zoomIn = () => setScale(prev => Math.min(3, prev + 0.2)); 
	const zoomOut = () => setScale(prev => Math.max(0.5, prev - 0.2));

	return (
		<div style={{ textAlign: "center" }}>
			<div style={{ margin: "10px 0"}}>
				<Button onClick={prevPage} disabled={pageNum <= 1}>Prev</Button>
				<span style={{ margin: "0 10px" }}>
					Page {pageNum} of {totalPages || "?"}
				</span>
				<Button onClick={nextPage} disabled={pageNum >= totalPages}>Next</Button>
				<span style={{ marginLeft: "20px" }}>
					Jump to Page: 
					<Input
						style={{ width: "70px", marginLeft: "10px" }}
						min={1}
						type="number"
						max={totalPages || 1}
						value={inputPage}
						onChange={e => {
							const val = e.target.value;
							if (val === "") {
								setInputPage("");
							} else {
								const numVal = Number(val);
								if (numVal >= 1 && numVal <= (totalPages || 1)) {
									setInputPage(numVal);
								}
							}
						}}
						onPressEnter={e => {
							const val = e.target.value;
							const numVal = Number(val);
							if (numVal >= 1 && numVal <= (totalPages || 1)) {
								setPageNum(numVal);
							} else {
								setInputPage(pageNum);
							}
						}}
					/>
				</span>
				<span style={{ marginLeft: "20px" }}>
					<span>Zoom:</span>
					<button className="btn-zoom" onClick={zoomOut} ><MinusOutlined /></button>
					<button className="btn-zoom" onClick={zoomIn} ><PlusOutlined /></button>
				</span>
				<Button
					style={{ marginLeft: 10, display: isbn ? "inline-block" : "none" }}
					onClick={() => window.open(`/drc/isbn/${isbn}`, "_blank", "noopener,noreferrer")}
				>
					Open in New Tab
				</Button>
			</div>	
			<Spin spinning={!pdf}>
				<div
					ref={containerRef}
					style={{ width: "100%", overflow: "scroll", height: url_isbn ? "auto" : "620px" }}
				>
					<div style={{ display: "flex", justifyContent: "center", minWidth: "min-content" }}>
						<canvas onContextMenu={(e) => e.preventDefault()} ref={canvasRef} style={{ border: "1px solid #ccc", display: pdf ? "block" : "none" }} />
					</div>
				</div>
			</Spin>
		</div>
	);
};

export default PDFViewer;
